<template>
  <div class="wrap-nt-autocomplete">
    <div class="nt-autocomplete"
      @blur="onBlur"
      @keydown="onKeyDown"
      :class="[searchingClass, themeClass, stateClass]">
      <!-- Form -->
      <a href="javascript:void(0)" class="nt-autocomplete__form" ref="form" @keyup.enter="openMenu">
        <!-- Icon -->
        <div class="nt-autocomplete__icon" @click="onClickToActivate" id="btn-search">
          <svg>
            <use xlink:href="#icon-search"></use>
          </svg>
        </div>

        <input
          type="text"
          ref="search"
          :placeholder="placeholder"
          v-model="text"
          class="input"
          @input="onInput" @focus="onFocus" @blur="onBlur" @keyup="onKeyup" @mouseover="current = -1"
        />
        <!-- Input -->
        <!-- <slot name="input" :text="text" :onInput="onInput" :onBlur="onBlur" :onFocus="onFocus" :keyup="onKeyup"></slot> -->
        <!-- Close button -->
        <div class="nt-autocomplete__icon" v-if="collapsible && !collapsed" @click="onClickToClose">
          <svg class="close">
            <use xlink:href="#icon-close"></use>
          </svg>
        </div>
        <slot name="innerbutton" :_goSearch="goSearch" :_option="typeView" :_changeOption="typeViewClick"></slot>
      </a>
      <!-- Results opened v-if="opened" -->
      <div v-show="opened" class="nt-autocomplete__results" ref="results">
        <!-- <slot name="results" :text="text" :data="dataSource" :_changeSelected="changeSelected"></slot> -->
        <!-- v-show="item.Text !== text" -->
        <div v-for="(item, index) in dataSource" :key="item.ItemId.Guid"
            class="item"
            :class="{ 'active' : index == current }"
            @mouseover="current = index" @mousedown="changeSelected(item.Text, 'precise')"
          >
          <nt-decorate :value="item.Text" :pattern="text"></nt-decorate>
          <svg>
            <use xlink:href="#icon-arrow-right"></use>
          </svg>
        </div>
        <div v-if="bottomlink" @mouseover="current=-1" class="item item-bottom" @mousedown="openBottomLink()">{{bottomlink.label}}</div>
      </div>
    </div>
    <!-- Button -->
    <div @click="changeSelected(text)">
      <slot name="button"></slot>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import uri from '../../../utils/uri'
import { debounce } from 'lodash'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'nt-autocomplete',
  props: {
    uriData: {
      type: String,
      required: true
    },
    uriRedirect: {
      type: String
    },
    collapsible: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String
    },
    placeholder: {
      type: String,
      default: ''
    },
    bottomlink: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      text: '',
      dataSource: [],
      collapsed: true,
      stateClass: 'inactive',
      showResults: false,
      option: 0,
      current: -1
    }
  },
  computed: {
    ...mapGetters([
      'scrollY',
      'typeView',
      'parameters'
    ]),
    themeClass() {
      return {
        [`nt-autocomplete--${this.theme}`]: !!this.theme
      }
    },
    searchingClass() {
      return {
        'nt-autocomplete--searching': this.collapsible && !this.collapsed || !this.collapsible
      }
    },
    opened() {
      return this.showResults && !!this.dataSource.length
    }
  },
  watch: {
    text: debounce(function (value) {
      if (value.length >= 2) {
        this.pushGTM()
      }

      if (!value.length) {
        this.dataSource = []
      }
      if (value.length < 2) return

      this.getData(value)
    }, 500),
    collapsed(value) {
      if (value) {
        this.text = ''
        this.showResults = false
      }
    },
    parameters(params) {
      if (this.uriRedirect) return
      this.text = params.querysearch
    },
    scrollY() {
      this.changeResultPosition()
    },
    dataSource() {
      this.$nextTick(() => {
        this.changeResultPosition()
      })
    },
    showResults(val) {
      this.$nextTick(() => {
        this.changeResultPosition()
      })

      if (val && !!this.isOnMobile()) {
        document.body.classList.add('no-scroll')
      } else {
        document.body.classList.remove('no-scroll')
      }
    }
  },
  created() {
    if (!this.uriRedirect) {
      const { querysearch } = uri.getQuery()

      if (!querysearch) return

      this.text = querysearch
      this.changeText(this.text)
    }
  },
  methods: {
    ...mapActions([
      'changeTypeView',
      'changeText',
      'changeSearchType'
    ]),
    getData(param) {
      axios.get(this.uriData, {
        params: {
          QuerySearch: param
        }
      }).then((response) => {
        this.dataSource = response.data
      })
    },
    onKeyDown(event) {
      switch (event.keyCode) {
        case 13:
          this.onEnter()
          break
        case 38:
          this.moveToUp()
          break
        case 40:
          this.moveToDown()
          break
        default:
          break
      }
    },
    onEnter() {
      if (this.current < 0) return

      this.text = this.dataSource[this.current].Text
      this.current = -1
    },
    moveToUp() {
      const curr = this.current - 1

      if (curr >= -1) {
        this.current--
      } else {
        this.current = this.dataSource.length - 1
      }
    },
    moveToDown() {
      const curr = this.current + 1
      const limit = this.dataSource.length - 1

      if (curr <= limit) {
        this.current = curr
      } else {
        this.current = -1
      }
    },
    openMenu() {
      if (this.collapsible && this.collapsed) {
        this.$refs.search.focus()
        this.collapsed = !this.collapsed
        this.$parent.$emit('openSearch', !this.collapsed)
      }
    },
    onClickToActivate() {
      if (this.collapsible && this.collapsed) {
        this.$refs.search.focus()
        this.collapsed = !this.collapsed
        this.$parent.$emit('openSearch', !this.collapsed)
      } else {
        this.goSearch()
      }
    },
    onClickToClose() {
      this.$refs.search.focus()
      this.collapsed = !this.collapsed
      this.$parent.$emit('openSearch', !this.collapsed)
    },
    onInput() {
      this.text = event.target.value
      this.showResults = true
    },
    onFocus() {
      this.showResults = true
    },
    onBlur() {
      this.showResults = false
    },
    onKeyup() {
      if (event.keyCode === 13) {
        this.goSearch()
      }
    },
    isOnMobile() {
      return (navigator.userAgent.match(/Android/i)
        || navigator.userAgent.match(/webOS/i)
        || navigator.userAgent.match(/iPhone/i)
        || navigator.userAgent.match(/iPad/i)
        || navigator.userAgent.match(/iPod/i)
        || navigator.userAgent.match(/BlackBerry/i)
        || navigator.userAgent.match(/Windows Phone/i)
      )
    },
    openBottomLink() {
      window.location.href = this.bottomlink.url
    },
    changeResultPosition() {
      const { form, results } = this.$refs
      const formBounds = form.getBoundingClientRect()
      const resultsBounds = results.getBoundingClientRect()

      if (resultsBounds.height < 1) {
        this.stateClass = 'inactive'
        return
      }

      const positionY = window.innerHeight - formBounds.top - 140
      const size = resultsBounds.height + formBounds.height

      if ((size > positionY) && (resultsBounds.height < formBounds.top)) {
        this.stateClass = 'top'
      } else {
        this.stateClass = 'bottom'
      }
    },
    changeSelected(value, searchType) {
      this.showResults = false
      this.changeSearchType(searchType)
      if (this.uriRedirect) {
        this.redirect(value)
      } else {
        this.text = value
        this.changeText(this.text)
      }
    },
    redirect(value) {
      let url = this.uriRedirect

      if (value) {
        url = `${url}?querysearch=${value}`
      }
      window.location.href = url
    },
    pushGTM() {
      window.dataLayer.push({
        event: 'search',
        name: this.text || ''
      })
    },
    goSearch() {
      this.changeSelected(this.text)
    },
    typeViewClick(value) {
      this.option = value
      this.changeTypeView(value)
    }
  }
}
</script>
